import { useQuery } from '@apollo/client';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import Button from '../../../core/components/Button.tsx';
import EmptyStage from '../../../core/components/EmptyStage.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import Loader from '../../../core/components/Loader.tsx';
import Page from '../../../core/components/Page';
import { useToaster } from '../../../core/components/Toast.tsx';
import { useApplicationContext } from '../../../core/contexts/ApplicationContext.tsx';
import classnames from '../../../core/utils/classnames.tsx';
import { canManageAgents } from '../../../users/utils.ts';
import { graphqlErrorMessage } from '../../errors.ts';
import { GET_CONVERSATION_SCHEDULES, GET_MY_TRIGGERS, GET_SHARED_SCHEDULES } from '../../queries.ts';
import { SidebarToggle } from '../SidebarToggle.tsx';
import { ScheduleCard, SharedScheduleCard, TriggerCard } from './AutomationCard.tsx';
import { ScheduleDetail } from './ScheduleDetail.tsx';

const PlusIcon = withIcon(faPlus);

interface AutomationsPageProps {
    scheduleId?: string;
    onOpenSidebar: () => void;
    onNewAutomation: () => void;
    onEditSchedule: (schedule: any) => void;
    onEditTrigger: (trigger: any) => void;
    canEditTriggers?: boolean;
}

const AutomationList = ({ onOpenSidebar, onNewAutomation, onEditSchedule, onEditTrigger, canEditTriggers }) => {
    const { t } = useTranslation('chat');
    const { publishToast } = useToaster();
    const { user } = useApplicationContext();
    const onError = (error) =>
        publishToast({ description: graphqlErrorMessage(error, t('automations.toasts.error')), status: 'error' });

    const { data: schedulesData, loading: schedulesLoading } = useQuery(GET_CONVERSATION_SCHEDULES, { onError });
    // email automations are agent configuration, served only to users who may manage agents
    const { data: triggersData, loading: triggersLoading } = useQuery(GET_MY_TRIGGERS, {
        skip: !canManageAgents(user),
        onError,
    });
    // schedules other people send to this user (ENG-11387), read-only, below their own
    const { data: sharedData, loading: sharedLoading } = useQuery(GET_SHARED_SCHEDULES, { onError });
    const schedules = schedulesData?.agentConversationSchedules || [];
    const triggers = triggersData?.myTriggers || [];
    const shared = sharedData?.sharedSchedules || [];
    const own = schedules.length > 0 || triggers.length > 0;
    const empty = !own && shared.length === 0;
    const loading = (schedulesLoading || triggersLoading || sharedLoading) && empty;

    return (
        <div className="flex min-w-0 flex-1 flex-col">
            <Page.Header className="justify-between">
                <Page.HeaderTitle>{t('automations.title')}</Page.HeaderTitle>
                <div className="flex items-center gap-2">
                    {!empty && (
                        <Page.HeaderButton type="button" onClick={onNewAutomation}>
                            <PlusIcon />
                            {t('automations.new')}
                        </Page.HeaderButton>
                    )}
                    <SidebarToggle onClick={onOpenSidebar} />
                </div>
            </Page.Header>
            <Page.Content lowered className="flex flex-col gap-4">
                {loading ? (
                    <Loader />
                ) : empty ? (
                    <EmptyStage
                        title={t('automations.emptyTitle')}
                        description={t(canEditTriggers ? 'automations.empty' : 'automations.emptyScheduleOnly')}
                        action={
                            <Button variant="primary" onClick={onNewAutomation}>
                                <PlusIcon className="mr-1.5" />
                                {t('automations.new')}
                            </Button>
                        }
                    />
                ) : (
                    <>
                        {own && (
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                                {schedules.map((schedule) => (
                                    <ScheduleCard key={schedule.id} schedule={schedule} onEdit={onEditSchedule} />
                                ))}
                                {triggers.map((trigger) => (
                                    <TriggerCard
                                        key={trigger.id}
                                        trigger={trigger}
                                        canEdit={canEditTriggers}
                                        onEdit={onEditTrigger}
                                    />
                                ))}
                            </div>
                        )}
                        {shared.length > 0 && (
                            <>
                                <h2
                                    className={classnames('m-0 text-sm font-medium text-content-strong', own && 'mt-4')}
                                >
                                    {t('automations.sharedWithMe')}
                                </h2>
                                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                                    {shared.map((schedule) => (
                                        <SharedScheduleCard key={schedule.id} schedule={schedule} />
                                    ))}
                                </div>
                            </>
                        )}
                    </>
                )}
            </Page.Content>
        </div>
    );
};

/** Automations (schedules and email automations, ENG-11296) open as a page in place of the thread, like projects;
 *  the conversation sidebar stays. */
export const AutomationsPage = ({
    scheduleId,
    onOpenSidebar,
    onNewAutomation,
    onEditSchedule,
    onEditTrigger,
    canEditTriggers = false,
}: AutomationsPageProps) =>
    scheduleId ? (
        <ScheduleDetail
            key={scheduleId}
            scheduleId={scheduleId}
            onEdit={onEditSchedule}
            onOpenSidebar={onOpenSidebar}
        />
    ) : (
        <AutomationList
            onOpenSidebar={onOpenSidebar}
            onNewAutomation={onNewAutomation}
            onEditSchedule={onEditSchedule}
            onEditTrigger={onEditTrigger}
            canEditTriggers={canEditTriggers}
        />
    );
